<template>
  <div class="search-filters d-flex flex-wrap">
    <b-dropdown
      class="filter-field"
      variant="outline-secondary"
      menu-class="menu-shadow scrollable text-medium"
      toggle-class="rounded-pill field"
    >
      <template slot="button-content">
        <span :title="filterDetails.team.name" class="d-flex overflow-hidden text-medium">
          <span class="text-secondary">Team: </span>
          <span class="text-truncate ml-1"
            ><span>{{ filterDetails.team.name }}</span></span
          >
        </span>
      </template>
      <b-dropdown-item v-for="team in teams" @click="onFilter('teamId', team.id)" :key="team.id">
        <i class="child-org-icon" v-if="team.isOrgLike && team.id !== subOrgId"></i>
        {{ team.name }}
      </b-dropdown-item>
    </b-dropdown>
    <single-select
      :options="memberships"
      :value="filterDetails.owner"
      bg-theme="document"
      @select="onFilter('ownerId', $event && $event.userId)"
      placeholder="Choose an owner"
    >
      <template v-slot:default="item">
        <div class="d-flex user-pill">
          <span class="text-secondary">Owner:</span>
          <user-short class="pl-2" :user-id="item.value.userId" length="18"></user-short>
          <span class="user-text text-truncate ml-2" :title="item.value.label">{{
            item.value.label
          }}</span>
        </div>
      </template>
      <template v-slot:listItem="option">
        <div class="d-flex dropdown-item-container">
          <user-short class="pl-2" :user-id="option.option.userId" length="18"></user-short>
          <span class="pl-2 user-text">{{ option.option.label }}</span>
        </div>
      </template>
    </single-select>
    <b-dropdown
      class="filter-field"
      variant="outline-secondary"
      menu-class="menu-shadow scrollable text-medium"
      toggle-class="rounded-pill field"
    >
      <template slot="button-content">
        <span :title="filterDetails.team.name" class="d-flex overflow-hidden text-medium">
          <span class="text-secondary">Type: </span>
          <span class="text-truncate ml-1"
            ><span>{{ filterDetails.resultTypes.name }}</span></span
          >
        </span>
      </template>
      <b-dropdown-item
        v-for="type in typeOptions"
        @click="onFilter('resultTypes', type.id)"
        :key="type.id"
        >{{ type.name }}
      </b-dropdown-item>
    </b-dropdown>
    <b-dropdown
      class="filter-field"
      variant="outline-secondary"
      menu-class="menu-shadow scrollable text-medium"
      toggle-class="rounded-pill field"
    >
      <template slot="button-content">
        <span :title="filterDetails.team.name" class="d-flex overflow-hidden text-medium">
          <span class="text-secondary">Timeline: </span>
          <span class="text-truncate ml-1"
            ><span>{{ filterDetails.timeline.name }}</span></span
          >
        </span>
      </template>
      <b-dropdown-item
        v-for="timeline in timelineOptions"
        @click="onFilter('timeline', timeline.id)"
        :key="timeline.id"
        >{{ timeline.name }}
      </b-dropdown-item>
    </b-dropdown>
  </div>
</template>

<script>
import SingleSelect from '@/components/form/SingleSelect'
import { featureFlags } from '@/constants/featureFlags'
import { filterIntervals, intervalWithId } from '@/interval'
import FeaturesMixin from '@/mixins/FeaturesMixin'
import * as R from 'ramda'
import { BDropdown, BDropdownItem } from 'bootstrap-vue'
import UserShort from '@/components/UserShort'
import { mapActions } from 'vuex'

const defaultFilterState = {
  team: { name: 'All' },
  resultTypes: { name: 'All' },
  timeline: { name: 'All' }
}

export default {
  name: 'SpotlightSearchFilters',
  components: {
    BDropdown,
    BDropdownItem,
    UserShort,
    SingleSelect
  },
  props: ['orgId', 'subOrgId', 'filterData'],
  mixins: [FeaturesMixin],
  data() {
    return {
      timelineOptions: [intervalWithId('any'), ...filterIntervals()]
    }
  },
  computed: {
    typeOptions() {
      const isTasksFeatureEnabled = this.getFeatureFlagValue(this.orgId, featureFlags.ALLOW_TASKS)
      return [
        { name: 'All' },
        { name: 'Goals and KRs', id: 'goals,krs' },
        { name: 'Projects', id: 'projects' },
        {
          name: isTasksFeatureEnabled ? 'Initiatives and Tasks' : 'Initiatives',
          id: 'initiatives'
        },
        { name: 'Comments', id: 'comments' }
      ]
    },
    memberships() {
      const memberships = [...this.$store.getters.teamMemberships(this.subOrgId)].sort((a, b) => {
        return a.user.displayName.toLowerCase().localeCompare(b.user.displayName.toLowerCase())
      })
      return [...memberships].map(membership => ({
        ...membership,
        label: membership.user.displayName,
        id: membership.userId
      }))
    },
    parentTeam() {
      return this.$store.getters.teamById(this.subOrgId)
    },
    teams() {
      return [
        defaultFilterState.team,
        this.parentTeam,
        ...this.$store.getters
          .userTeamsByOrgIdSubOrgId(this.orgId, this.subOrgId)
          .filter(x => !x.isArchived && x.parentId)
      ]
    },
    filterDetails() {
      const filterDetails = R.toPairs(this.filterData).map(([field, fieldValue]) => {
        switch (field) {
          case 'ownerId':
            return ['owner', this.memberships.find(R.propEq('userId', fieldValue))]
          case 'teamId':
            return ['team', this.teams.find(R.propEq('id', fieldValue))]
          case 'resultTypes':
            return ['resultTypes', this.typeOptions.find(R.propEq('id', fieldValue))]
          case 'timeline':
            return ['timeline', this.timelineOptions.find(R.propEq('id', fieldValue))]
          default:
            return [field, fieldValue]
        }
      })
      return { ...defaultFilterState, ...R.fromPairs(filterDetails) }
    }
  },
  methods: {
    ...mapActions(['getTeamMemberships']),
    onFilter(field, fieldValue) {
      this.emitChange({ field, fieldValue })
    },
    emitChange(filterData) {
      this.$emit('change', filterData)
    }
  },
  mounted() {
    this.getTeamMemberships({ orgId: this.orgId, teamId: this.subOrgId })
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';

/deep/ .field {
  border: 1px solid $quaternary;
  background: inherit !important;
  color: inherit !important;
  max-width: 240px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  &:focus {
    border-color: $prime;
  }
}
.filter-field {
  white-space: nowrap;
  min-width: 150px;
  margin-bottom: 8px;
}
.search-filters {
  padding: 0 30px;
  margin-bottom: 12px;
  gap: 14px;
}
</style>
